import AlumniMentorInvitations from './AlumniMentorInvitations';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URL } from './config';
import { AlumniIdentityActions } from './AlumniRegister';
import './alumni.css';
import './AlumniDashboard.css';
const ROOT = `${API_URL}/api/alumni/auth`;
const FIELDS = [
  ['FullName', 'Full name'], ['GraduationYear', 'Graduation year'], ['Email', 'Email'], ['LinkedInURL', 'LinkedIn URL'],
  ['CurrentPostalCode', 'ZIP / postal code'], ['CurrentCity', 'City'],
  ['CurrentState', 'State'], ['CurrentCountry', 'Country'],
  ['EmploymentStatus', 'Employment status'], ['Company', 'Company'], ['Role', 'Role'],
];
const TABS = [
  { id: 'overview', label: 'Overview', icon: '▦' },
  { id: 'profile', label: 'My Profile', icon: '◉' },
  { id: 'directory', label: 'Alumni Directory', icon: '⌕' },
  { id: 'connections', label: 'Connections', icon: '♧' },
  { id: 'mentorship', label: 'Mentorship', icon: '♧' },
  { id: 'security', label: 'Account & Security', icon: '◇' },
];
const imageUrl = (path) => !path ? '' : /^https?:\/\//i.test(path) ? path : `${API_URL}${path.startsWith('/') ? '' : '/'}${path}`;
const alumniId = value => String(value ?? '').trim();
const peerStatus = (connections, id, person = {}) => {
  const target = alumniId(id);
  const matches = row => [
    row.PeerAlumniID, row.AlumniID, row.OtherAlumniID,
    row.ConnectedAlumniID, row.TargetAlumniID,
    row.RequesterAlumniID, row.RecipientAlumniID,
  ].some(value => alumniId(value) === target && target !== '');

  // Prefer the relationship returned by the connections endpoint.
  if ((connections.connected || []).some(matches)) return 'Connected';
  if ((connections.outgoing || []).some(matches)) return 'Request Pending';
  if ((connections.incoming || []).some(matches)) return 'Request Pending';

  // Some directory endpoints include the relationship directly.
  const status = String(person.ConnectionStatus || person.connectionStatus || '').toLowerCase();
  if (['accepted', 'connected'].includes(status)) return 'Connected';
  if (['pending', 'requested', 'sent'].includes(status)) return 'Request Pending';
  return 'Connect';
};
const formatDate = (date) => date ? new Date(date).toLocaleDateString() : 'Not available';
async function alumniRequest(path, token, options = {}) {
  const response = await fetch(`${ROOT}${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(typeof options.body === 'string' ? { 'Content-Type': 'application/json' } : {}),
      ...(options.headers || {}),
    },
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok)
    throw new Error(body.error || `Request failed (${response.status})`);
  return body;
}
export default function AlumniDashboard() {
  const navigate = useNavigate();
  const token = localStorage.getItem('alumniToken');
  const [tab, setTab] = useState('overview');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [skills, setSkills] = useState('');
  const [preferences, setPreferences] = useState({});
  const [saving, setSaving] = useState(false);
  const [directory, setDirectory] = useState([]);
  const [directorySearch, setDirectorySearch] = useState('');
  const [directoryPage, setDirectoryPage] = useState(1);
  const [directoryTotal, setDirectoryTotal] = useState(0);
  const [directoryLoading, setDirectoryLoading] = useState(false);
  const [directoryLoaded, setDirectoryLoaded] = useState(false);
  const [connectionsLoading, setConnectionsLoading] = useState(true);
  const [connectingId, setConnectingId] = useState(null);
  const loadProfile = useCallback(async () => {
    if (!token) {
      navigate('/alumni/login', { replace: true });
      return;
    }
    try {
      const response = await fetch(`${ROOT}/me`, { headers: { Authorization: `Bearer ${token}` } });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          localStorage.removeItem('alumniToken');
          navigate('/alumni/login', { replace: true });
          return;
        }
        throw new Error(body.error || 'Unable to load alumni profile');
      }
      setData(body);
      setDraft(body.profile || {});
      setSkills((body.skills || []).join(', '));
      setPreferences(body.preferences || {});
      setError('');
    }
    catch (err) {
      setError(err.message);
    }
    finally {
      setLoading(false);
    }
  }, [token, navigate]);
  useEffect(() => { loadProfile(); }, [loadProfile]);
  const profile = data?.profile || {};
  const identities = Array.isArray(data?.identities) ? data.identities : [];
  const passkeys = Array.isArray(data?.passkeys) ? data.passkeys : [];
  const providers = [...new Set(identities.map((x) => String(x.Provider || '').toLowerCase()).filter(Boolean))];
  const methodCount = providers.length + (passkeys.length ? 1 : 0);
  const saveProfile = async () => {
    setSaving(true);
    setError('');
    setNotice('');
    try {
      await alumniRequest('/profile', token, {
        method: 'PATCH',
        body: JSON.stringify({ ...draft, preferences, skills: skills.split(',').map((x) => x.trim()).filter(Boolean) }),
      });
      setEditing(false);
      setNotice('Your profile has been updated.');
      await loadProfile();
    }
    catch (err) {
      setError(err.message);
    }
    finally {
      setSaving(false);
    }
  };
  const uploadPhoto = async (event) => {
    const file = event.target.files?.[0];
    if (!file)
      return;
    if (!['image/jpeg', 'image/png'].includes(file.type) || file.size > 5000 * 1024) {
      setError('Choose a JPG or PNG photo under 5 MB.');
      return;
    }
    const form = new FormData();
    form.append('photo', file);
    try {
      setError('');
      await alumniRequest('/photo', token, { method: 'POST', body: form });
      setNotice('Profile photo updated.');
      await loadProfile();
    }
    catch (err) {
      setError(err.message);
    }
    finally {
      event.target.value = '';
    }
  };
  const renamePasskey = async (key) => {
    const name = window.prompt('Name this passkey (e.g., MacBook Touch ID)', key.FriendlyName || '');
    if (!name?.trim())
      return;
    try {
      await alumniRequest(`/passkeys/${encodeURIComponent(key.CredentialID)}`, token, {
        method: 'PATCH', body: JSON.stringify({ name: name.trim() }),
      });
      setNotice('Passkey renamed.');
      await loadProfile();
    }
    catch (err) {
      setError(err.message);
    }
  };
  const removePasskey = async (key) => {
    if (!window.confirm('Remove this passkey? You may need another sign-in method.'))
      return;
    try {
      await alumniRequest(`/passkeys/${encodeURIComponent(key.CredentialID)}`, token, { method: 'DELETE' });
      setNotice('Passkey removed.');
      await loadProfile();
    }
    catch (err) {
      setError(err.message);
    }
  };
  const searchDirectory = useCallback(async (page = 1, search = directorySearch) => {
    setDirectoryLoading(true);
    setError('');
    try {
      const result = await alumniRequest(`/directory?q=${encodeURIComponent(search.trim())}&limit=20&page=${page}`, token);
      setDirectory(Array.isArray(result.rows) ? result.rows : []);
      setDirectoryTotal(Number(result.total) || 0);
      setDirectoryPage(page);
      setDirectoryLoaded(true);
    }
    catch (err) {
      setError(err.message);
    }
    finally {
      setDirectoryLoading(false);
    }
  }, [directorySearch, token]);
  const connect = async (id) => {
    if (connectionsLoading || connectingId !== null || peerStatus(connections, id) !== 'Connect') return;
    setConnectingId(alumniId(id));
    try {
      setError('');
      await alumniRequest('/connections', token, { method: 'POST', body: JSON.stringify({ alumniId: id }) });
      setNotice('Connection request sent successfully.');
      await loadConnections();
    }
    catch (err) {
      setError(err.message);
    } finally {
      setConnectingId(null);
    }
  };
  const [connections, setConnections] = useState({incoming:[],outgoing:[],connected:[]});
  const loadConnections = useCallback(async () => {
    setConnectionsLoading(true);
    try {
      const response = await alumniRequest('/connections', token);
      setConnections({
        incoming: Array.isArray(response.incoming) ? response.incoming : [],
        outgoing: Array.isArray(response.outgoing) ? response.outgoing : [],
        connected: Array.isArray(response.connected) ? response.connected : [],
      });
    } catch(e) { setError(e.message); }
    finally { setConnectionsLoading(false); }
  }, [token]);
  useEffect(() => { if(tab === 'connections') loadConnections(); }, [tab,loadConnections]);
  useEffect(() => {
    if (tab === 'directory' && token) {
      if (!directoryLoaded) searchDirectory(1);
      loadConnections();
    }
  }, [tab, directoryLoaded, token, searchDirectory, loadConnections]);
  const respondConnection = async (id,decision) => {
    try { await alumniRequest(`/connections/${id}/respond`,token,{method:'POST',body:JSON.stringify({decision})});
      setNotice(`Request ${decision}.`); await loadConnections(); }
    catch(e) { setError(e.message); }
  };
  const changeTab = (id) => { setTab(id); setError(''); setNotice(''); };
  const signOut = () => { localStorage.removeItem('alumniToken'); navigate('/alumni/login'); };
  const location = [profile.CurrentCity, profile.CurrentState, profile.CurrentCountry].filter(Boolean).join(', ') || 'Not provided';
  return (<div className="al-bg ad-page">
    <main className="al-dashboard ad-shell">
      <header className="ad-hero">
        <div>
          <div className="ad-eyebrow">
            UNIFLOO · ALUMNI CONNECT
          </div>
          <h1>
            Alumni Dashboard
          </h1>
          <p>
            Welcome back,
            {profile.FullName || 'Alumnus'}
            . Manage your profile, network, mentorship, and account.
          </p>
        </div>
        <button type="button" className="ad-signout" onClick={signOut}>
          Sign out
        </button>
      </header>
      {error && <div className="al-error ad-message" role="alert">
        {error}
      </div>}
      {notice && <div className="al-success ad-message" role="status">
        {notice}
      </div>}
      <div className="ad-content">
        <nav className="ad-tabs" aria-label="Alumni dashboard sections">
          {TABS.map((item) => (<button key={item.id} type="button" role="tab" aria-selected={tab === item.id} className={`ad-tab ${tab === item.id ? 'ad-tab-active' : ''}`} onClick={() => changeTab(item.id)}>
            <span className="ad-tab-icon" aria-hidden="true">
              {item.icon}
            </span>
            {item.label}
          </button>))}
        </nav>
        {loading && <div className="ad-empty">
          Loading your alumni dashboard…
        </div>}
        {!loading && tab === 'overview' && (<div className="ad-section">
          <div className="ad-section-head">
            <div>
              <h2>
                Account overview
              </h2>
              <p>
                A quick view of your alumni account and network.
              </p>
            </div>
            <button className="ad-button-outline" onClick={() => changeTab('profile')}>
              View profile
            </button>
          </div>
          <div className="ad-stats">
            <div className="ad-stat">
              <span>
                Current location
              </span>
              <strong>
                {location}
              </strong>
            </div>
            <div className="ad-stat">
              <span>
                Profile last confirmed
              </span>
              <strong>
                {profile.LastConfirmedAt ? formatDate(profile.LastConfirmedAt) : 'Needs confirmation'}
              </strong>
            </div>
            <div className="ad-stat">
              <span>
                Sign-in methods
              </span>
              <strong>
                {methodCount}
              </strong>
            </div>
          </div>
          <div className="ad-overview-grid">
            <section className="ad-card">
              <div className="ad-card-heading">
                <h3>
                  Profile snapshot
                </h3>
                <button className="ad-text-button" onClick={() => changeTab('profile')}>
                  Edit profile →
                </button>
              </div>
              <div className="ad-profile-summary">
                {profile.PhotoURL ? <img className="ad-photo" src={imageUrl(profile.PhotoURL)} alt="Alumni profile" /> : <div className="ad-photo ad-photo-placeholder">
                  {(profile.FullName || 'A').charAt(0)}
                </div>}
                <div>
                  <strong>
                    {profile.FullName || 'Alumnus'}
                  </strong>
                  <span>
                    {profile.Role || 'Alumni'}
                    {profile.Company ? ` · ${profile.Company}` : ''}
                  </span>
                  <span>
                    {profile.Email || 'Email not provided'}
                  </span>
                </div>
              </div>
              <div className="ad-details">
                <div>
                  <span>
                    Campus
                  </span>
                  <b>
                    {profile.CampusCode || '—'}
                  </b>
                </div>
                <div>
                  <span>
                    Skills
                  </span>
                  <b>
                    {(data?.skills || []).join(', ') || 'Not added'}
                  </b>
                </div>
              </div>
            </section>
            <section className="ad-card">
              <h3>
                Quick actions
              </h3>
              <p className="ad-muted">
                Jump directly to the tools you use most.
              </p>
              <div className="ad-actions">
                <button onClick={() => changeTab('directory')}>
                  Explore alumni directory
                  <span>
                    →
                  </span>
                </button>
                <button onClick={() => changeTab('mentorship')}>
                  Review mentor invitations
                  <span>
                    →
                  </span>
                </button>
                <button onClick={() => changeTab('security')}>
                  Manage sign-in methods
                  <span>
                    →
                  </span>
                </button>
              </div>
            </section>
          </div>
        </div>)}
        {!loading && tab === 'connections' && <div className="ad-section">
          <div className="ad-section-head"><div><h2>Alumni connections</h2><p>Review incoming requests and your alumni network.</p></div><button className="ad-button-outline" onClick={loadConnections}>Refresh</button></div>
          {[["Incoming requests","incoming"],["My connections","connected"],["Sent requests","outgoing"]].map(([title,key]) => <section className="ad-card" key={key}>
            <h3>{title} ({connections[key]?.length || 0})</h3>
            {!connections[key]?.length && <p className="ad-muted">No {title.toLowerCase()}.</p>}
            <div className="ad-directory-grid">{(connections[key] || []).map(person => <article className="ad-card ad-person" key={`${person.RequesterAlumniID}-${person.RecipientAlumniID}`}>
              <div className="ad-profile-summary">{person.PhotoURL ? <img className="ad-photo" src={imageUrl(person.PhotoURL)} alt="" /> : <div className="ad-photo ad-photo-placeholder">{(person.FullName || 'A')[0]}</div>}<div><strong>{person.FullName}</strong><span>{[person.Company,person.CurrentCity].filter(Boolean).join(' · ')}</span></div></div>
              {person.Role && <p><b>Role:</b> {person.Role}</p>}
              <div className="ad-tags">{!!person.MentorOptIn && <span>Available to mentor</span>}{!!person.ReadyToHire && <span>Ready to hire</span>}</div>
              {!!person.skills?.length && <p><b>Skills:</b> {person.skills.join(', ')}</p>}
              {key === 'connected' && <div className="ad-contact">{person.Email && <p><b>Email:</b> <a href={`mailto:${person.Email}`}>{person.Email}</a></p>}{person.WhatsAppE164 && <><p><b>Mobile / WhatsApp:</b> <a href={`tel:${person.WhatsAppE164}`}>{person.WhatsAppE164}</a></p><a className="ad-button-outline ad-whatsapp" href={`https://wa.me/${person.WhatsAppE164.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer">Message on WhatsApp</a></>}</div>}
              {key === 'incoming' ? <div className="ad-form-actions"><button className="ad-button-primary" onClick={() => respondConnection(person.RequesterAlumniID,'accepted')}>Accept</button><button className="ad-button-outline" onClick={() => respondConnection(person.RequesterAlumniID,'declined')}>Decline</button></div> : <span className="ad-count">{key === 'connected' ? 'Connected' : 'Pending'}</span>}
            </article>)}</div>
          </section>)}
        </div>}
        {!loading && tab === 'profile' && (<div className="ad-section">
          <div className="ad-section-head">
            <div>
              <h2>
                My profile
              </h2>
              <p>
                Keep your information current so fellow alumni can find you.
              </p>
            </div>
            <button className="ad-button-primary" onClick={() => {
              if (editing) {
                setDraft(profile);
                setSkills((data?.skills || []).join(', '));
              } setEditing(!editing);
            }}>
              {editing ? 'Cancel editing' : 'Edit profile'}
            </button>
          </div>
          <section className="ad-card">
            <div className="ad-profile-summary ad-profile-large">
              {profile.PhotoURL ? <img className="ad-photo" src={imageUrl(profile.PhotoURL)} alt="Alumni profile" /> : <div className="ad-photo ad-photo-placeholder">
                {(profile.FullName || 'A').charAt(0)}
              </div>}
              <div>
                <strong>
                  {profile.FullName || 'Alumnus'}
                </strong>
                <span>
                  {profile.Company || 'Company not provided'}
                  {profile.Role ? ` · ${profile.Role}` : ''}
                </span>
                <span>
                  {location}
                </span>
              </div>
            </div>
            <div className="ad-details" style={{ marginBottom: 16 }}>
              <div><span>Registered WhatsApp number</span><b>{profile.WhatsAppE164 || 'Not available'}</b></div>
              <div><span>Verification</span><b>Registered account number (read-only)</b></div>
            </div>
            <label className="ad-upload">
              Update profile photo (JPG or PNG, max 5 MB)
              <input type="file" accept="image/jpeg,image/png" onChange={uploadPhoto} />
            </label>
            {editing ? <div className="ad-form">
              <div className="ad-form-grid">
                {FIELDS.map(([key, label]) => <label key={key}>
                  {label}
                  <input value={draft[key] ?? ''} onChange={(e) => setDraft((old) => ({ ...old, [key]: e.target.value }))} />
                </label>)}
                <label className="ad-wide">
                  Skills (comma-separated)
                  <input value={skills} onChange={(e) => setSkills(e.target.value)} />
                </label>
              </div>
              <div className="ad-form-actions">
                <button className="ad-button-outline" onClick={() => setEditing(false)}>
                  Cancel
                </button>
                <div className="ad-pref-controls" style={{display:'flex',gap:18,flexWrap:'wrap',margin:'12px 0'}}>{[['MentorOptIn','Available to mentor'],['ReadyToHire','Ready to hire (jobs and internships)']].map(([key,label]) => <label key={key} style={{display:'flex',alignItems:'center',gap:7,fontWeight:700}}><input type="checkbox" checked={!!preferences[key]} onChange={e=>setPreferences(old=>({...old,[key]:e.target.checked}))}/>{label}</label>)}</div>
                  <button className="ad-button-primary" disabled={saving} onClick={saveProfile}>
                  {saving ? 'Saving…' : 'Save changes'}
                </button>
              </div>
            </div> : <div className="ad-detail-grid">
              {[['Email', profile.Email], ['Campus', profile.CampusCode], ['LinkedIn', profile.LinkedInURL], ['Graduation year', profile.GraduationYear], ['City', profile.CurrentCity], ['State', profile.CurrentState], ['Country', profile.CurrentCountry], ['Employment status', profile.EmploymentStatus], ['Company', profile.Company], ['Role', profile.Role], ['Skills', (data?.skills || []).join(', ')], ['Available to mentor', data?.preferences?.MentorOptIn ? 'Yes' : 'No'], ['Ready to hire', data?.preferences?.ReadyToHire ? 'Yes' : 'No'], ['Last confirmed', formatDate(profile.LastConfirmedAt)]].map(([label, value]) => <div key={label}>
                <span>
                  {label}
                </span>
                {label === 'LinkedIn' && value ? <a href={value} target="_blank" rel="noreferrer">
                  View profile ↗
                </a> : <strong>
                  {value || '—'}
                </strong>}
              </div>)}
            </div>}
          </section>
        </div>)}
        {!loading && tab === 'directory' && (<div className="ad-section">
          <div className="ad-section-head">
            <div>
              <h2>
                Alumni directory
              </h2>
              <p>
                Find alumni by name, location, company, graduation year, sub-campus, degree, or branch.
              </p>
            </div>
            <span className="ad-count">
              {directoryTotal}
              matching alumni
            </span>
          </div>
          <section className="ad-card">
            <form className="ad-search" onSubmit={(e) => { e.preventDefault(); searchDirectory(1); }}>
              <input aria-label="Search alumni" placeholder="Search name, city, company, batch, campus, degree or branch" value={directorySearch} onChange={(e) => setDirectorySearch(e.target.value)} />
              <button className="ad-button-primary" disabled={directoryLoading} type="submit">
                {directoryLoading ? 'Searching…' : 'Search'}
              </button>
            </form>
          </section>
          {directoryLoading ? <div className="ad-empty">
            Loading alumni…
          </div> : <>
            <div className="ad-directory-grid">
              {directory.map((person) => <article className="ad-card ad-person" key={person.AlumniID}>
                <div className="ad-profile-summary">
                  {person.PhotoURL ? <img className="ad-photo" src={imageUrl(person.PhotoURL)} alt="" loading="lazy" /> : <div className="ad-photo ad-photo-placeholder">
                    {(person.FullName || 'A').charAt(0)}
                  </div>}
                  <div>
                    <strong>
                      {person.FullName}
                    </strong>
                    <span>
                      {person.Company || 'Alumni'}
                    </span>
                    <span>
                      {[person.CurrentCity, person.CurrentState].filter(Boolean).join(', ') || 'Location not listed'}
                    </span>
                  </div>
                </div>
                <div className="ad-person-meta">
                  {[person.GraduationYear && `Batch ${person.GraduationYear}`, person.SubCampusCode, person.DegreeCode, person.BranchCode].filter(Boolean).join(' · ') || 'Academic details not listed'}
                </div>
                {Array.isArray(person.skills) && person.skills.length > 0 && <div className="ad-tags">
                  {person.skills.slice(0, 5).map((skill) => <span key={skill}>
                    {skill}
                  </span>)}
                </div>}
                {(() => {
                  const status = peerStatus(connections, person.AlumniID, person);
                  const disabled = connectionsLoading || connectingId !== null || status !== 'Connect';
                  return (
                    <button
                      type="button"
                      className="ad-button-outline ad-person-connect"
                      disabled={disabled}
                      aria-label={`${person.FullName}: ${status}`}
                      onClick={() => connect(person.AlumniID)}
                    >
                      {connectingId === alumniId(person.AlumniID)
                        ? 'Sending…'
                        : connectionsLoading ? 'Checking…' : status}
                    </button>
                  );
                })()}
              </article>)}
            </div>
            {!directory.length && <div className="ad-empty">
              No alumni match your search. Try a different name, location, or batch.
            </div>}
            {directoryTotal > 20 && <div className="ad-pagination">
              <button className="ad-button-outline" disabled={directoryPage <= 1} onClick={() => searchDirectory(directoryPage - 1)}>
                Previous
              </button>
              <span>
                Page
                {directoryPage}
                of
                {Math.ceil(directoryTotal / 20)}
              </span>
              <button className="ad-button-outline" disabled={directoryPage >= Math.ceil(directoryTotal / 20)} onClick={() => searchDirectory(directoryPage + 1)}>
                Next
              </button>
            </div>}
          </>}
        </div>)}
        {!loading && tab === 'mentorship' && <div className="ad-section">
          <div className="ad-section-head">
            <div>
              <h2>
                Mentorship
              </h2>
              <p>
                Review and respond to mentorship invitations from students.
              </p>
            </div>
          </div>
          <div className="ad-mentor-container">
            <AlumniMentorInvitations />
          </div>
        </div>}
        {!loading && tab === 'security' && (<div className="ad-section">
          <div className="ad-section-head">
            <div>
              <h2>
                Account & security
              </h2>
              <p>
                Manage secure sign-in options and registered passkeys.
              </p>
            </div>
          </div>
          <section className="ad-card">
            <h3>
              Sign-in methods
            </h3>
            <p className="ad-muted">
              Connect sign-in providers and add passkeys to access your alumni account securely.
            </p>
            <AlumniIdentityActions token={token} passkeyCount={passkeys.length} linkedProviders={providers} onPasskeyAdded={loadProfile} />
            <div className="ad-details">
              <div>
                <span>
                  Connected providers
                </span>
                <b>
                  {providers.join(', ') || 'None yet'}
                </b>
              </div>
              <div>
                <span>
                  Registered passkeys
                </span>
                <b>
                  {passkeys.length}
                </b>
              </div>
            </div>
          </section>
          <section className="ad-card">
            <h3>
              Registered passkeys
            </h3>
            {passkeys.length ? <div className="ad-passkey-list">
              {passkeys.map((key, index) => <div className="ad-passkey" key={key.CredentialID || index}>
                <div>
                  <strong>
                    {key.FriendlyName || [key.DeviceType, key.BackedUp ? 'Synced' : 'Device-bound'].filter(Boolean).join(' · ') || `Passkey ${index + 1}`}
                  </strong>
                  <span>
                    {key.LastUsedAt ? `Last used ${formatDate(key.LastUsedAt)}` : 'Not used recently'}
                  </span>
                </div>
                <div>
                  <button className="ad-button-outline" onClick={() => renamePasskey(key)}>
                    Rename
                  </button>
                  <button className="ad-button-danger" onClick={() => removePasskey(key)}>
                    Remove
                  </button>
                </div>
              </div>)}
            </div> : <p className="ad-muted">
              No passkeys registered yet. Add one using the sign-in options above.
            </p>}
          </section>
        </div>)}
      </div>
    </main>
  </div>);
}
